<script setup>
/* global axios */
// Baileys (QR ile baglanan WhatsApp) gelen kutusunun baglanti paneli.
//
// Neden ayri bir sekme: bu kanalin sagligi Chatwoot'un bildigi hicbir yerde
// gorunmuyordu. Oturum koptugunda ekranda tek bir isaret yoktu; kullanici
// ancak "mesajlar gelmiyor" diyerek fark ediyordu. Panel durumu, numarayi ve
// gerektiginde QR'i tek yerde gosterir.
//
// Ham 'axios' paketi ice AKTARILMAZ: Chatwoot kimlik basliklarini
// window.axios uzerinde tutar, ham kutuphane 401 doner.
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const route = useRoute();

const uc = computed(
  () => `/api/v1/accounts/${route.params.accountId}/baileys/${props.inbox.id}`
);

const yukleniyor = ref(true);
const calisiyor = ref(false);
const hata = ref('');
const durum = ref(null);

let zamanlayici = null

// Gecidin dondugu ham durumlari kullanicinin anlayacagi bir dile cevirir.
// Ham 'qr_pending' gibi bir metni ekrana basmak kullaniciya hicbir sey
// anlatmaz.
const DURUM_ANAHTARLARI = {
  connected: 'BAILEYS_DURUM.DURUM.BAGLI',
  connecting: 'BAILEYS_DURUM.DURUM.BAGLANIYOR',
  starting: 'BAILEYS_DURUM.DURUM.BASLATILIYOR',
  qr_pending: 'BAILEYS_DURUM.DURUM.QR_BEKLIYOR',
  disconnected: 'BAILEYS_DURUM.DURUM.KOPUK',
  expired: 'BAILEYS_DURUM.DURUM.SURESI_DOLDU',
  yok: 'BAILEYS_DURUM.DURUM.YOK',
};

const DURUM_RENKLERI = {
  connected: 'text-n-teal-11 bg-n-alpha-1',
  connecting: 'text-n-amber-11 bg-n-alpha-1',
  starting: 'text-n-amber-11 bg-n-alpha-1',
  qr_pending: 'text-n-amber-11 bg-n-alpha-1',
  disconnected: 'text-n-ruby-11 bg-n-alpha-1',
  expired: 'text-n-ruby-11 bg-n-alpha-1',
};

const ham = computed(() => durum.value?.durum || 'yok');
const bagliMi = computed(() => ham.value === 'connected');
const qrVar = computed(() => Boolean(durum.value?.qr));

const durumMetni = computed(() =>
  t(DURUM_ANAHTARLARI[ham.value] || 'BAILEYS_DURUM.DURUM.YOK')
);
const durumRengi = computed(
  () => DURUM_RENKLERI[ham.value] || 'text-n-slate-11 bg-n-alpha-1'
);

const hataMetni = e => e.response?.data?.error || e.message;

const durumuOku = async () => {
  try {
    const { data } = await axios.get(`${uc.value}/durum`);
    durum.value = data;
    hata.value = '';
  } catch (e) {
    hata.value = hataMetni(e);
  } finally {
    yukleniyor.value = false;
  }
};

// QR birkac saniyede bir tazelenir ve baglanti durumu disaridan degisebilir
// (telefondan cihaz cikarmak gibi). Sayfa acikken duzenli sorguluyoruz, ama
// bagliyken daha seyrek: bagli bir oturumda saniyede bir sormanin anlami yok.
const zamanlayiciyiKur = () => {
  if (zamanlayici) clearInterval(zamanlayici);
  zamanlayici = setInterval(durumuOku, bagliMi.value ? 15000 : 4000);
};

const baglan = async () => {
  calisiyor.value = true;
  hata.value = '';
  try {
    const { data } = await axios.post(`${uc.value}/baslat`);
    durum.value = data;
    zamanlayiciyiKur();
  } catch (e) {
    hata.value = hataMetni(e);
  } finally {
    calisiyor.value = false;
  }
};

// Oturumu kapatmak telefondaki eslesmeyi de siler: yeniden baglanmak icin
// QR tekrar okutulmalidir. Geri alinamaz oldugu icin once onay aliyoruz.
const cikisYap = async () => {
  if (!window.confirm(t('BAILEYS_DURUM.CIKIS_ONAYI'))) return;

  calisiyor.value = true;
  hata.value = '';
  try {
    await axios.post(`${uc.value}/durdur`, { wipe: 'true' });
    useAlert(t('BAILEYS_DURUM.CIKIS_YAPILDI'));
    await durumuOku();
    zamanlayiciyiKur();
  } catch (e) {
    hata.value = hataMetni(e);
  } finally {
    calisiyor.value = false;
  }
};

onMounted(async () => {
  await durumuOku();
  zamanlayiciyiKur();
});

onUnmounted(() => {
  if (zamanlayici) clearInterval(zamanlayici);
});
</script>

<template>
  <div class="mx-6 max-w-4xl flex flex-col gap-6">
    <div v-if="yukleniyor" class="flex justify-center py-10">
      <Spinner />
    </div>

    <template v-else>
      <div class="p-5 border rounded-lg border-n-weak bg-n-surface-1">
        <div class="flex flex-wrap items-center gap-3">
          <span
            class="px-2 py-1 text-xs font-medium rounded"
            :class="durumRengi"
          >
            {{ durumMetni }}
          </span>

          <span v-if="durum?.telefon" class="text-sm text-n-slate-12">
            +{{ durum.telefon }}
          </span>
          <span v-if="durum?.ad" class="text-sm text-n-slate-11">
            {{ durum.ad }}
          </span>

          <div class="ml-auto flex items-center gap-2">
            <Button
              size="sm"
              variant="faded"
              icon="i-lucide-refresh-cw"
              :label="t('BAILEYS_DURUM.YENILE')"
              :is-loading="calisiyor"
              @click="durumuOku"
            />
            <Button
              v-if="!bagliMi"
              size="sm"
              icon="i-lucide-qr-code"
              :label="t('BAILEYS_DURUM.BAGLAN')"
              :is-loading="calisiyor"
              @click="baglan"
            />
            <Button
              v-else
              size="sm"
              variant="faded"
              icon="i-lucide-log-out"
              :label="t('BAILEYS_DURUM.CIKIS')"
              :is-loading="calisiyor"
              @click="cikisYap"
            />
          </div>
        </div>

        <p class="mt-3 text-sm text-n-slate-11">
          {{
            bagliMi
              ? t('BAILEYS_DURUM.ACIKLAMA_BAGLI')
              : t('BAILEYS_DURUM.ACIKLAMA_KOPUK')
          }}
        </p>

        <!-- Gecidin bildirdigi son hata: "neden baglanmadi" sorusunun cevabi
             genelde burada duruyor. -->
        <p v-if="durum?.sonHata" class="mt-2 text-sm text-n-ruby-11">
          {{ t('BAILEYS_DURUM.SON_HATA') }}: {{ durum.sonHata }}
        </p>
        <p v-if="hata" class="mt-2 text-sm text-n-ruby-11">{{ hata }}</p>
      </div>

      <div
        v-if="qrVar"
        class="p-5 text-center border rounded-lg border-n-weak bg-n-surface-1"
      >
        <h3 class="mb-1 text-sm font-medium text-n-slate-12">
          {{ t('BAILEYS_DURUM.QR_BASLIK') }}
        </h3>
        <p class="mb-4 text-sm text-n-slate-11">
          {{ t('BAILEYS_DURUM.QR_ACIKLAMA') }}
        </p>
        <img
          :src="durum.qr"
          alt=""
          class="inline-block w-56 h-56 bg-white rounded"
        />
      </div>
    </template>
  </div>
</template>
